/**
 * Lectura de los claims del access token.
 *
 * **No se verifica la firma acá y no se puede.** El secreto HS256 vive en el
 * servidor; un cliente que "validara" el token estaría validando contra nada.
 * Lo que se lee de acá sirve **sólo para pintar la UI** (nombre, rol, tenant)
 * y para saber cuándo conviene refrescar. La autorización real la hace la API
 * en cada request: si alguien edita su `role` en localStorage, la UI le va a
 * mostrar un botón y la API le va a devolver 403 con `insufficient_role`.
 *
 * Por eso las guardas por rol de este dashboard son **ergonomía, no
 * seguridad**. Está anotado también en el ADR porque es la clase de cosa que
 * alguien asume al revés seis meses después.
 */
import type { Role } from '@/api/types';

export interface AccessClaims {
  /** `sub` — id de `api_user`. La API lo manda como string (RFC 7519). */
  userId: number;
  /** `tid` — el que va a RLS del lado del servidor. */
  tenantId: number;
  /** `tenant` — slug, para la UI. */
  tenant: string;
  email: string;
  role: Role;
  name: string | null;
  /** `jti`. */
  tokenId: string;
  /** Epoch en **milisegundos** (el claim viene en segundos). */
  expiresAt: number;
  issuedAt: number;
}

const ROLES: readonly string[] = ['admin', 'analyst', 'viewer'];

function decodeBase64Url(segment: string): string | null {
  try {
    const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
    // El JWT es UTF-8 y `atob` devuelve latin-1: sin esto, un `name` con
    // acentos —"Administración"— sale roto en la cabecera.
    const bytes = Uint8Array.from(binary, (c) => c.codePointAt(0) ?? 0);
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

/**
 * `null` si el token no es un JWT nuestro o le falta un claim obligatorio.
 * Un token que no se puede leer se trata como "no hay sesión": es lo mismo
 * que va a decidir la API.
 */
export function decodeAccessClaims(token: string): AccessClaims | null {
  const parts = token.split('.');
  const payload = parts.length === 3 ? parts[1] : undefined;
  if (payload === undefined) return null;

  const json = decodeBase64Url(payload);
  if (json === null) return null;

  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }

  const sub = raw['sub'];
  const tid = raw['tid'];
  const tenant = raw['tenant'];
  const email = raw['email'];
  const role = raw['role'];
  const jti = raw['jti'];
  const exp = raw['exp'];
  const iat = raw['iat'];

  if (
    typeof sub !== 'string' ||
    typeof tid !== 'number' ||
    typeof tenant !== 'string' ||
    typeof email !== 'string' ||
    typeof role !== 'string' ||
    !ROLES.includes(role) ||
    typeof jti !== 'string' ||
    typeof exp !== 'number' ||
    typeof iat !== 'number'
  ) {
    return null;
  }

  const userId = Number.parseInt(sub, 10);
  if (!Number.isFinite(userId)) return null;

  const name = raw['name'];
  return {
    userId,
    tenantId: tid,
    tenant,
    email,
    role: role as Role,
    name: typeof name === 'string' && name.length > 0 ? name : null,
    tokenId: jti,
    expiresAt: exp * 1000,
    issuedAt: iat * 1000,
  };
}
